<template>
  <div class="slot-game" :class="{ 'is-mobile': isMobile }">
    <!-- Landscape Orientation Warning -->
    <div v-if="showLandscapeWarning" class="landscape-warning" data-testid="landscape-warning">
      <div class="landscape-warning-content">
        <div class="landscape-warning-icon"><AppIcon name="mobile-rotate" :size="32" /></div>
        <div class="landscape-warning-text">{{ $t('games.rotate_device') }}</div>
      </div>
    </div>

    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-slots" :size="18" /> {{ slotConfig?.name ?? $t('games.slots.tag') }}
        <span class="game-tag">{{ $t('games.slots.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas Area -->
      <div class="game-canvas-area" ref="canvasContainer">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <!-- Loading Screen -->
        <div v-if="isLoading" class="slot-loading-screen" data-testid="loading-screen">
          <div class="loading-content">
            <div class="loading-logo"><AppIcon name="game-slots" :size="48" /></div>
            <div class="loading-title">{{ loadingTitle }}</div>
            <div class="loading-bar-container" data-testid="loading-bar">
              <div
                class="loading-bar-fill"
                :style="{ width: loadingProgress + '%' }"
                data-testid="loading-bar-fill"
              ></div>
            </div>
            <div class="loading-status" data-testid="loading-status">{{ loadingStatus }}</div>
            <div v-if="loadingError" class="loading-error" data-testid="loading-error">
              <p>{{ loadingError }}</p>
              <button class="btn-retry" @click="retryLoading">{{ $t('games.retry') }}</button>
            </div>
          </div>
        </div>

        <!-- PixiJS Canvas Mount -->
        <div ref="pixiMount" class="slot-canvas-mount" v-show="!isLoading" data-testid="pixi-mount"></div>

        <!-- Win Display Overlay -->
        <Transition name="win-display">
          <div
            v-if="winDisplay.visible"
            class="win-display-overlay"
            :class="winDisplay.tier"
            data-testid="win-display"
          >
            <div class="win-display-amount" data-testid="win-amount">${{ formatCurrency(winDisplay.amount) }}</div>
            <div v-if="winDisplay.detail" class="win-display-detail" data-testid="win-detail">{{ winDisplay.detail }}</div>
          </div>
        </Transition>
      </div>

      <!-- Game Controls Sidebar -->
      <div class="game-sidebar" data-testid="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            data-testid="tab-bet"
            @click="activeTab = 'bet'"
          >
            {{ $t('games.bet_tab') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'auto' }"
            data-testid="tab-auto"
            @click="activeTab = 'auto'"
          >
            {{ $t('games.auto_tab') }}
          </button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            data-testid="tab-rules"
            @click="activeTab = 'rules'"
          >
            {{ $t('games.rules_tab') }}
          </button>
        </div>

        <!-- BET TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'bet' }" data-testid="bet-tab">
          <!-- Coin Value Selector -->
          <label class="ctrl-label">{{ $t('games.slots.coin_value') }}</label>
          <div class="coin-value-row" data-testid="coin-value-row">
            <button
              class="coin-btn"
              data-testid="coin-down"
              :disabled="!canCoinDown"
              @click="onCoinDown"
            >
              -
            </button>
            <span class="coin-value-display" data-testid="coin-value">${{ formatCurrency(currentCoinValue) }}</span>
            <button
              class="coin-btn"
              data-testid="coin-up"
              :disabled="!canCoinUp"
              @click="onCoinUp"
            >
              +
            </button>
          </div>

          <!-- Lines Display (payline games only) -->
          <div v-if="slotConfig?.grid?.type === 'paylines'" class="lines-display">
            <label class="ctrl-label">{{ $t('games.slots.lines') }}</label>
            <span class="lines-value" data-testid="lines-value">{{ slotConfig.grid.payline_count }}</span>
          </div>

          <!-- Total Stake -->
          <div class="stake-display" data-testid="stake-display">
            <label class="ctrl-label">{{ $t('games.slots.total_stake') }}</label>
            <span class="stake-value">${{ formatCurrency(currentTotalStake) }}</span>
          </div>

          <!-- Last Win -->
          <div v-if="lastWinAmount > 0" class="stake-display last-win-display" data-testid="last-win">
            <label class="ctrl-label">{{ $t('games.slots.last_win') }}</label>
            <span class="stake-value gold">${{ formatCurrency(lastWinAmount) }}</span>
          </div>

          <!-- Bet Presets -->
          <div class="bet-shortcuts" data-testid="bet-presets">
            <button class="bet-short" data-testid="preset-min" @click="onBetPreset('min')">{{ $t('games.min_label') }}</button>
            <button class="bet-short" data-testid="preset-5x" @click="onBetPreset('5x')">5x</button>
            <button class="bet-short" data-testid="preset-10x" @click="onBetPreset('10x')">10x</button>
            <button class="bet-short" data-testid="preset-max" @click="onBetPreset('max')">{{ $t('games.max_label') }}</button>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Turbo Toggle -->
          <div class="toggle-row">
            <span class="toggle-label">{{ $t('games.slots.turbo') }}</span>
            <button
              class="toggle"
              :class="{ on: turboEnabled }"
              data-testid="turbo-toggle"
              @click="onToggleTurbo"
            >
              <span class="toggle-thumb"></span>
            </button>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Spin Button -->
          <button
            class="btn-action"
            :class="{ 'btn-stop': autoSpinActive }"
            data-testid="spin-button"
            :disabled="spinButtonState === 'disabled'"
            @click="onSpinClick"
          >
            {{ spinButtonText }}
          </button>

          <!-- Game Info -->
          <div class="stat-pills" v-if="slotConfig">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.slots.type_label') }}</div>
              <div class="sp-value">{{ slotConfig.grid.type === 'paylines' ? $t('games.slots.lines_count', { n: slotConfig.grid.payline_count }) : slotConfig.grid.type === 'ways' ? $t('games.slots.ways_243') : $t('games.slots.cluster') }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.slots.volatility') }}</div>
              <div class="sp-value gold">{{ slotConfig.ui.volatility }}</div>
            </div>
          </div>
        </div>

        <!-- AUTO TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'auto' }" data-testid="auto-tab">
          <!-- Spin Count Selector -->
          <label class="ctrl-label">{{ $t('games.slots.number_of_spins') }}</label>
          <div class="auto-count-row" data-testid="auto-count-row">
            <button
              v-for="count in [10, 25, 50, 100]"
              :key="count"
              class="auto-count-btn"
              :class="{ selected: autoSpinCount === count }"
              :data-testid="'auto-count-' + count"
              @click="autoSpinCount = count"
            >
              {{ count }}
            </button>
            <button
              class="auto-count-btn"
              :class="{ selected: autoSpinCount === 0 }"
              data-testid="auto-count-inf"
              @click="autoSpinCount = 0"
            >
              &infin;
            </button>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Stop Conditions -->
          <label class="ctrl-label">{{ $t('games.slots.stop_conditions') }}</label>

          <div class="toggle-row">
            <span class="toggle-label">{{ $t('games.slots.stop_on_any_win') }}</span>
            <button
              class="toggle"
              :class="{ on: autoStopOnWin }"
              data-testid="auto-stop-win"
              @click="autoStopOnWin = !autoStopOnWin"
            >
              <span class="toggle-thumb"></span>
            </button>
          </div>

          <div class="toggle-row">
            <span class="toggle-label">{{ $t('games.slots.stop_on_bonus') }}</span>
            <button
              class="toggle"
              :class="{ on: autoStopOnBonus }"
              data-testid="auto-stop-bonus"
              @click="autoStopOnBonus = !autoStopOnBonus"
            >
              <span class="toggle-thumb"></span>
            </button>
          </div>

          <div class="auto-field">
            <label class="ctrl-label">{{ $t('games.slots.balance_below') }}</label>
            <div class="bet-input-wrap">
              <span class="bet-currency">$</span>
              <input
                class="auto-input"
                type="number"
                data-testid="auto-balance-below"
                v-model.number="autoStopBalanceBelow"
                min="0"
                step="1"
                placeholder="0 (disabled)"
              />
            </div>
          </div>

          <div class="auto-field">
            <label class="ctrl-label">{{ $t('games.slots.win_above') }}</label>
            <div class="bet-input-wrap">
              <span class="bet-currency">$</span>
              <input
                class="auto-input"
                type="number"
                data-testid="auto-win-above"
                v-model.number="autoStopWinAbove"
                min="0"
                step="1"
                placeholder="0 (disabled)"
              />
            </div>
          </div>

          <div class="ctrl-divider"></div>

          <!-- Start / Stop Auto Button -->
          <button
            v-if="!autoSpinActive"
            class="btn-action"
            data-testid="auto-start-btn"
            @click="onStartAutoSpin"
          >
            START AUTO SPIN
          </button>
          <button
            v-else
            class="btn-action btn-stop"
            data-testid="auto-stop-btn"
            @click="onStopAutoSpin"
          >
            STOP AUTO ({{ autoSpinRemaining > 0 ? autoSpinRemaining : '&infin;' }})
          </button>
        </div>

        <!-- RULES TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'rules' }" data-testid="rules-tab">
          <div class="rules-section" v-if="slotConfig">
            <div class="rules-title">{{ $t('games.slots.how_to_play') }}</div>
            <ul class="rules-list">
              <li class="rules-item">
                {{ slotConfig.grid.reels }} reels, {{ slotConfig.grid.rows }} rows
                <template v-if="slotConfig.grid.type === 'paylines'">
                  with {{ slotConfig.grid.payline_count }} paylines
                </template>
                <template v-else-if="slotConfig.grid.type === 'ways'">
                  with ways-to-win
                </template>
                <template v-else>
                  with cluster pays
                </template>
              </li>
              <li class="rules-item">Match 3+ symbols to win</li>
              <li class="rules-item" v-if="slotConfig.features.free_spins?.enabled">
                3+ Scatters trigger Free Spins
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Application, Container, Graphics, Sprite, Texture, Assets } from 'pixi.js'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import type { SlotConfig } from '@/types/slot'
import { ReelContainer } from './slot/ReelContainer'
import { SpinAnimator } from './slot/SpinAnimator'
import { WinPresenter } from './slot/WinPresenter'
import type { WinDisplayData } from './slot/WinPresenter'
import { GameControls } from './slot/GameControls'
import { useWalletStore } from '@/stores/wallet'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const walletStore = useWalletStore()

// ─── State ───
const soundEnabled = ref(false)
const slotConfig = ref<SlotConfig | null>(null)
const isLoading = ref(true)
const loadingProgress = ref(0)
const loadingStatus = ref('Initializing...')
const loadingError = ref<string | null>(null)

// ─── Mobile state ───
const MOBILE_BREAKPOINT = 600
const isMobile = ref(false)
const showLandscapeWarning = ref(false)
let orientationMediaQuery: MediaQueryList | null = null
let mobileMediaQuery: MediaQueryList | null = null

// ─── Game controls state ───
let gameControls: GameControls | null = null
const activeTab = ref<'bet' | 'auto' | 'rules'>('bet')
const currentCoinValue = ref(0)
const currentTotalStake = ref(0)
const currentCoinIndex = ref(0)
const canCoinUp = ref(false)
const canCoinDown = ref(false)
const spinButtonState = ref<'ready' | 'spinning' | 'disabled'>('ready')
const spinButtonText = ref('')
const turboEnabled = ref(false)
const autoSpinActive = ref(false)
const autoSpinRemaining = ref(0)
const autoSpinCount = ref(10)
const autoStopOnWin = ref(false)
const autoStopOnBonus = ref(false)
const autoStopBalanceBelow = ref(0)
const autoStopWinAbove = ref(0)

// ─── Win display state ───
const lastWinAmount = ref(0)
const winDisplay = ref<{
  visible: boolean
  amount: number
  detail: string
  tier: string
}>({ visible: false, amount: 0, detail: '', tier: '' })
let winDetailCycleTimer: ReturnType<typeof setInterval> | null = null
let winDisplayHideTimer: ReturnType<typeof setTimeout> | null = null

// ─── Computed ───
const slotId = computed(() => {
  const slug = route.params.slug as string
  return slug.replace(/-/g, '_')
})

const loadingTitle = computed(() => {
  if (slotConfig.value) return `Loading ${slotConfig.value.name}...`
  return 'Loading...'
})

// ─── PixiJS refs ───
const pixiMount = ref<HTMLDivElement | null>(null)
const canvasContainer = ref<HTMLDivElement | null>(null)
let app: Application | null = null
let bgContainer: Container | null = null
let logoContainer: Container | null = null
let reelContainer: Container | null = null
let reelDisplay: ReelContainer | null = null
let spinAnimator: SpinAnimator | null = null
let winPresenter: WinPresenter | null = null
let winOverlay: Container | null = null
let resizeObserver: ResizeObserver | null = null

// ─── Canvas dimensions ───
const MIN_CANVAS_WIDTH = 320
const MIN_CANVAS_HEIGHT = 180

// ─── Sync reactive state from GameControls ───
function syncControlsState(): void {
  if (!gameControls) return
  currentCoinValue.value = gameControls.coinValue
  currentTotalStake.value = gameControls.totalStake
  currentCoinIndex.value = gameControls.coinIndex
  canCoinUp.value = gameControls.canCoinUp
  canCoinDown.value = gameControls.canCoinDown
  spinButtonState.value = gameControls.spinButtonState
  turboEnabled.value = gameControls.turboEnabled
  autoSpinActive.value = gameControls.autoSpinActive
  autoSpinRemaining.value = gameControls.autoSpinRemaining

  if (gameControls.isSpinning) {
    spinButtonText.value = autoSpinActive.value
      ? `${t('games.spin')} (${autoSpinRemaining.value})`
      : t('games.spin')
  } else {
    spinButtonText.value = autoSpinActive.value
      ? `${t('games.stop_auto')} (${autoSpinRemaining.value})`
      : t('games.spin')
  }
}

// ─── Control actions ───
function onCoinUp(): void {
  if (!gameControls) return
  gameControls.coinUp()
  syncControlsState()
}

function onCoinDown(): void {
  if (!gameControls) return
  gameControls.coinDown()
  syncControlsState()
}

function onCoinSelect(index: number): void {
  if (!gameControls) return
  gameControls.setCoinIndex(index)
  syncControlsState()
}

function onBetPreset(preset: 'min' | '5x' | '10x' | 'max'): void {
  if (!gameControls) return
  switch (preset) {
    case 'min':
      gameControls.setMinBet()
      break
    case '5x':
      gameControls.setBetMultiple(5)
      break
    case '10x':
      gameControls.setBetMultiple(10)
      break
    case 'max':
      gameControls.setMaxBet()
      break
  }
  syncControlsState()
}

function onToggleTurbo(): void {
  if (!gameControls) return
  gameControls.toggleTurbo()
  syncControlsState()
}

function onSpinClick(): void {
  if (!gameControls) return

  // If auto-spinning, stop it
  if (gameControls.autoSpinActive) {
    gameControls.stopAutoSpin()
    syncControlsState()
    return
  }

  if (!gameControls.isSpinReady) return

  executeSpin()
}

// Last spin result — used by auto-spin stop conditions
let lastSpinResult: { isWin: boolean; payout: number; isBonusTrigger: boolean } | null = null

async function executeSpin(): Promise<void> {
  if (!gameControls || !spinAnimator || spinAnimator.isSpinning) return

  gameControls.setSpinning()
  syncControlsState()

  const config = slotConfig.value
  if (!config) return

  const slug = route.params.slug as string

  try {
    const res = await apiClient.post<{
      payout: number
      multiplier: number
      is_win: boolean
      outcome_data: {
        grid: string[][]
        stop_positions: number[]
        wins: Array<{
          symbol: string
          count: number
          payline_id?: number
          multiplier: number
          positions: { reel: number; row: number }[]
        }>
        triggers?: Record<string, unknown>
        total_multiplier: number
      }
      game_round_id: string
    }>(`/api/games/${slug}/play`, {
      amount: currentTotalStake.value.toFixed(2),
    })

    const { outcome_data, is_win, payout } = res.data
    const hasBonusTrigger = !!(
      outcome_data.triggers && Object.keys(outcome_data.triggers).length > 0
    )
    lastSpinResult = { isWin: is_win, payout, isBonusTrigger: hasBonusTrigger }

    const finalGrid = outcome_data.grid
    const reelCount = config.grid.reels

    // Visual scroll distances (stop_positions are strip indices, not scroll amounts)
    const targetPositions = Array.from(
      { length: reelCount },
      () => Math.floor(Math.random() * 20) + 5,
    )

    // Map server wins to WinDisplayData for the presenter
    const wins: WinDisplayData[] = (outcome_data.wins || []).map((win) => {
      const type: WinDisplayData['type'] =
        win.payline_id !== undefined
          ? 'payline'
          : config.grid.type === 'ways'
            ? 'ways'
            : 'cluster'
      return {
        type,
        positions: win.positions,
        paylineId: win.payline_id,
      }
    })

    // Retain full win data for display
    const spinWins: SpinWinData[] = (outcome_data.wins || []).map((w) => ({
      symbol: w.symbol,
      count: w.count,
      multiplier: w.multiplier,
      paylineId: w.payline_id,
    }))

    // Instant header balance update from the play response data
    walletStore.applyGameResult(currentTotalStake.value, payout)

    triggerSpin(targetPositions, finalGrid, wins, { payout, spinWins })
  } catch {
    // API failed — reset to ready state
    lastSpinResult = null
    gameControls.setReady()
    syncControlsState()
  }
}

function onSpinComplete(): void {
  if (!gameControls) return
  gameControls.setReady()
  syncControlsState()

  // Process auto-spin if active
  if (gameControls.autoSpinActive) {
    const result = lastSpinResult
    const balance = walletStore.totalBalance
    const shouldContinue = gameControls.processAutoSpin(
      result?.isWin ?? false,
      result?.payout ?? 0,
      balance,
      result?.isBonusTrigger ?? false,
    )
    syncControlsState()
    if (shouldContinue) {
      setTimeout(() => executeSpin(), gameControls!.turboEnabled ? 100 : 500)
    }
  }

  lastSpinResult = null
}

function onStartAutoSpin(): void {
  if (!gameControls) return
  gameControls.startAutoSpin({
    count: autoSpinCount.value,
    stopOnAnyWin: autoStopOnWin.value,
    stopOnBonusTrigger: autoStopOnBonus.value,
    stopOnBalanceBelow: autoStopBalanceBelow.value,
    stopOnWinAbove: autoStopWinAbove.value,
  })
  syncControlsState()
  executeSpin()
}

function onStopAutoSpin(): void {
  if (!gameControls) return
  gameControls.stopAutoSpin()
  syncControlsState()
}

function formatCurrency(amount: number): string {
  return amount.toFixed(2)
}

// ─── Win display ───
interface SpinWinData {
  symbol: string
  count: number
  multiplier: number
  paylineId?: number
}

function getWinTier(payout: number, bet: number): string {
  if (bet <= 0) return ''
  const ratio = payout / bet
  const thresholds = slotConfig.value?.ui.win_celebration_thresholds
  if (thresholds) {
    if (ratio >= (thresholds.ultra ?? 100)) return 'ultra'
    if (ratio >= (thresholds.mega ?? 50)) return 'mega'
    if (ratio >= (thresholds.big ?? 25)) return 'big'
    if (ratio >= (thresholds.nice ?? 5)) return 'nice'
  }
  return ''
}

function showWinDisplay(payout: number, wins: SpinWinData[]): void {
  clearWinDisplay()

  if (payout <= 0 || wins.length === 0) return

  const tier = getWinTier(payout, currentTotalStake.value)

  lastWinAmount.value = payout

  // Build detail strings for each win
  const details: string[] = wins.map((w) => {
    const symbolDef = slotConfig.value?.symbols.find((s) => s.id === w.symbol)
    const name = symbolDef?.name ?? w.symbol
    const lineLabel = w.paylineId !== undefined ? ` on Line ${w.paylineId + 1}` : ''
    return `${w.count}x ${name}${lineLabel} — ${w.multiplier}x`
  })

  winDisplay.value = {
    visible: true,
    amount: payout,
    detail: details[0] ?? '',
    tier,
  }

  // Cycle through individual win details if multiple wins
  if (details.length > 1) {
    let index = 1
    winDetailCycleTimer = setInterval(() => {
      winDisplay.value.detail = details[index % details.length]
      index++
    }, 1500)
  }

  // Auto-hide after win animation completes (longer for bigger wins)
  const displayMs = tier === 'ultra' ? 6000 : tier === 'mega' ? 5000 : tier === 'big' ? 4000 : 3000
  winDisplayHideTimer = setTimeout(() => {
    winDisplay.value.visible = false
    clearWinDetailCycle()
  }, displayMs)
}

function clearWinDisplay(): void {
  winDisplay.value.visible = false
  clearWinDetailCycle()
  if (winDisplayHideTimer) {
    clearTimeout(winDisplayHideTimer)
    winDisplayHideTimer = null
  }
}

function clearWinDetailCycle(): void {
  if (winDetailCycleTimer) {
    clearInterval(winDetailCycleTimer)
    winDetailCycleTimer = null
  }
}

// ─── Mobile / Orientation detection ───
function checkMobile(): void {
  isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT
}

function checkOrientation(): void {
  if (!isMobile.value) {
    showLandscapeWarning.value = false
    return
  }
  // Landscape = width > height on a mobile-sized screen
  showLandscapeWarning.value = window.innerWidth > window.innerHeight
}

function setupMobileDetection(): void {
  checkMobile()
  checkOrientation()

  // Use matchMedia for efficient breakpoint listening
  if (typeof window.matchMedia === 'function') {
    mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const onMobileChange = () => {
      checkMobile()
      checkOrientation()
    }
    mobileMediaQuery.addEventListener('change', onMobileChange)

    orientationMediaQuery = window.matchMedia('(orientation: landscape)')
    orientationMediaQuery.addEventListener('change', () => checkOrientation())
  }

  // Fallback: also listen to resize for orientation changes
  window.addEventListener('resize', checkOrientation)
}

function teardownMobileDetection(): void {
  window.removeEventListener('resize', checkOrientation)
  if (mobileMediaQuery) {
    mobileMediaQuery.removeEventListener('change', checkMobile)
    mobileMediaQuery = null
  }
  if (orientationMediaQuery) {
    orientationMediaQuery.removeEventListener('change', checkOrientation)
    orientationMediaQuery = null
  }
}

// ─── Load slot config ───
async function loadSlotConfig(): Promise<SlotConfig | null> {
  loadingStatus.value = 'Loading configuration...'
  loadingProgress.value = 5

  try {
    const res = await apiClient.get<{ data: SlotConfig }>(`/api/slots/${slotId.value}/config`)
    const config = res.data.data ?? (res.data as unknown as SlotConfig)
    // Validate the response is actually a slot config (not HTML from catch-all route)
    if (config && typeof config === 'object' && 'grid' in config) {
      return config
    }
    return null
  } catch {
    return null
  }
}

// ─── Asset loading ───
async function loadAssets(config: SlotConfig): Promise<void> {
  loadingStatus.value = 'Loading assets...'
  loadingProgress.value = 20

  const assetBundle: Record<string, string> = {}
  const assetKeys: string[] = []

  // Background
  if (config.assets.background) {
    assetBundle['slot-bg'] = config.assets.background
    assetKeys.push('slot-bg')
  }

  // Logo
  if (config.assets.logo) {
    assetBundle['slot-logo'] = config.assets.logo
    assetKeys.push('slot-logo')
  }

  // Symbol sprites
  if (config.assets.symbol_sprites) {
    assetBundle['slot-symbols'] = config.assets.symbol_sprites
    assetKeys.push('slot-symbols')
  }

  if (assetKeys.length === 0) {
    loadingProgress.value = 90
    loadingStatus.value = 'No assets to load'
    return
  }

  // Register assets with PixiJS Assets system
  const bundleName = `slot-${config.id}`
  Assets.addBundle(bundleName, Object.fromEntries(assetKeys.map((k) => [k, assetBundle[k]])))

  try {
    await Assets.loadBundle(bundleName, (progress: number) => {
      // Map progress from 20% → 90%
      loadingProgress.value = 20 + Math.round(progress * 70)
      if (progress < 0.5) {
        loadingStatus.value = 'Loading symbols...'
      } else if (progress < 0.8) {
        loadingStatus.value = 'Loading background...'
      } else {
        loadingStatus.value = 'Finalizing...'
      }
    })
  } catch {
    // Assets may not exist on disk yet — proceed with fallbacks
    loadingStatus.value = 'Using placeholder assets...'
    loadingProgress.value = 90
  }
}

// ─── PixiJS initialization ───
function initPixi(): boolean {
  const mount = pixiMount.value
  if (!mount) return false

  const containerRect = canvasContainer.value?.getBoundingClientRect()
  const w = containerRect?.width || mount.clientWidth || 800
  const h = containerRect?.height || mount.clientHeight || 450

  app = new Application({
    width: Math.max(w, MIN_CANVAS_WIDTH),
    height: Math.max(h, MIN_CANVAS_HEIGHT),
    backgroundColor: 0x0d1b2a,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  })

  mount.appendChild(app.view as HTMLCanvasElement)

  // Create scene layers
  bgContainer = new Container()
  bgContainer.name = 'background'
  app.stage.addChild(bgContainer)

  logoContainer = new Container()
  logoContainer.name = 'logo'
  app.stage.addChild(logoContainer)

  reelContainer = new Container()
  reelContainer.name = 'reels'
  app.stage.addChild(reelContainer)

  winOverlay = new Container()
  winOverlay.name = 'win-overlay'
  app.stage.addChild(winOverlay)

  return true
}

// ─── Responsive resize ───
function setupResize(): void {
  const mount = pixiMount.value
  if (!mount || !app) return

  resizeObserver = new ResizeObserver(() => {
    if (!app || !mount) return
    const container = canvasContainer.value
    if (!container) return

    const rect = container.getBoundingClientRect()
    const newW = Math.max(rect.width, MIN_CANVAS_WIDTH)
    const newH = Math.max(rect.height, MIN_CANVAS_HEIGHT)

    if (newW > 0 && newH > 0) {
      app.renderer.resize(newW, newH)
      layoutScene(newW, newH)
    }
  })

  resizeObserver.observe(mount)
}

// ─── Scene layout — position background and containers ───
function layoutScene(width: number, height: number): void {
  if (!bgContainer || !app) return

  // Update background to fill canvas
  const bgSprite = bgContainer.children[0] as Sprite | undefined
  if (bgSprite && bgSprite instanceof Sprite) {
    scaleToFill(bgSprite, width, height)
  }

  // Position and resize reel container
  if (reelContainer && slotConfig.value) {
    // Mobile: reels use more width and are positioned higher to leave room for controls
    const mobile = isMobile.value
    const reelAreaWidth = mobile ? width * 0.92 : width * 0.7
    const reelAreaHeight = mobile ? height * 0.88 : height * 0.75

    // Check if logo is present to shift reels down and make room
    const hasLogo = logoContainer && logoContainer.children.length > 0
    const logoMaxHeight = mobile ? 0 : height * 0.12
    const reelYBase = mobile ? height * 0.04 : (height - reelAreaHeight) / 2

    reelContainer.x = (width - reelAreaWidth) / 2
    reelContainer.y = hasLogo && !mobile ? reelYBase + logoMaxHeight * 0.5 : reelYBase

    if (reelDisplay) {
      reelDisplay.resize(reelAreaWidth, reelAreaHeight)
    }

    // Win overlay matches reel container position and scale
    if (winOverlay && reelDisplay) {
      winOverlay.x = reelContainer.x + reelDisplay.container.x
      winOverlay.y = reelContainer.y + reelDisplay.container.y
      winOverlay.scale.set(reelDisplay.container.scale.x, reelDisplay.container.scale.y)
    }

    // Position logo centered above the reels
    if (hasLogo && !mobile) {
      const logoSprite = logoContainer!.children[0] as Sprite
      const texW = logoSprite.texture.width
      const texH = logoSprite.texture.height

      if (texW > 0 && texH > 0) {
        // Scale logo to fit within the available space above the reels
        const maxW = reelAreaWidth * 0.6
        const maxH = logoMaxHeight * 0.85
        const logoScale = Math.min(maxW / texW, maxH / texH, 1)
        logoSprite.scale.set(logoScale)

        // Center horizontally, place just above the reel area with a small gap
        logoSprite.x = width / 2
        logoSprite.y = reelContainer.y - 8
      }

      logoContainer!.visible = true
    } else if (logoContainer) {
      // Hide logo on mobile to preserve vertical space for reels
      logoContainer.visible = false
    }
  }
}

// ─── Render themed background ───
function renderBackground(config: SlotConfig): void {
  if (!bgContainer || !app) return
  bgContainer.removeChildren()

  const w = app.renderer.width / (app.renderer.resolution || 1)
  const h = app.renderer.height / (app.renderer.resolution || 1)

  // Try to use loaded background texture
  let bgTexture: Texture | null = null
  try {
    bgTexture = Assets.get<Texture>('slot-bg')
  } catch {
    bgTexture = null
  }

  if (bgTexture && bgTexture !== Texture.EMPTY) {
    const bgSprite = new Sprite(bgTexture)
    scaleToFill(bgSprite, w, h)
    bgContainer.addChild(bgSprite)
  } else {
    // Fallback: themed gradient background using config colors
    const bg = new Graphics()
    const primaryColor = parseColor(config.ui.primary_color)
    const secondaryColor = parseColor(config.ui.secondary_color)

    // Dark base
    bg.beginFill(secondaryColor)
    bg.drawRect(0, 0, w, h)
    bg.endFill()

    // Accent overlay
    bg.beginFill(primaryColor, 0.15)
    bg.drawRect(0, 0, w, h)
    bg.endFill()

    // Vignette effect (darker edges)
    bg.beginFill(0x000000, 0.3)
    bg.drawRect(0, 0, w, h * 0.15)
    bg.endFill()
    bg.beginFill(0x000000, 0.3)
    bg.drawRect(0, h * 0.85, w, h * 0.15)
    bg.endFill()

    bgContainer.addChild(bg)
  }
}

// ─── Render logo above reels ───
function renderLogo(): void {
  if (!logoContainer) return
  logoContainer.removeChildren()

  let logoTexture: Texture | null = null
  try {
    logoTexture = Assets.get<Texture>('slot-logo')
  } catch {
    logoTexture = null
  }

  if (logoTexture && logoTexture !== Texture.EMPTY) {
    const logoSprite = new Sprite(logoTexture)
    logoSprite.anchor.set(0.5, 1)
    logoContainer.addChild(logoSprite)
  }
}

// ─── Reel setup ───
function setupReels(config: SlotConfig): void {
  if (!reelContainer || !app) return

  const symbolWidth = 120
  const symbolHeight = 120
  const reelGap = 8

  reelDisplay = new ReelContainer(
    {
      reelCount: config.grid.reels,
      rowCount: config.grid.rows,
      symbolWidth,
      symbolHeight,
      reelGap,
    },
    config.symbols,
  )

  // Try to load symbol sprite sheet
  try {
    const symbolTexture = Assets.get<Texture>('slot-symbols')
    if (symbolTexture && symbolTexture !== Texture.EMPTY) {
      reelDisplay.loadSpriteSheet(symbolTexture, config.symbols.length)
    }
  } catch {
    // Fallback to placeholder symbols
  }

  reelContainer.addChild(reelDisplay.container)

  // Set initial display symbols (cycle through available symbols)
  const initialGrid: string[][] = []
  for (let r = 0; r < config.grid.reels; r++) {
    const col: string[] = []
    for (let row = 0; row < config.grid.rows; row++) {
      const symbolIndex = (r * config.grid.rows + row) % config.symbols.length
      col.push(config.symbols[symbolIndex].id)
    }
    initialGrid.push(col)
  }
  reelDisplay.setSymbols(initialGrid)

  // Create spin animator with staggered reel stops
  spinAnimator = new SpinAnimator({
    reelCount: config.grid.reels,
    symbolHeight,
    spinDuration: 2000,
    staggerDelay: 150,
    bounceOvershoot: 0.1,
  })

  // Initialize game controls
  gameControls = new GameControls({
    coinValues: config.bet_config.coin_values,
    defaultBet: config.bet_config.default_bet,
    minBet: config.bet_config.min_bet,
    maxBet: config.bet_config.max_bet,
    paylineCount: config.grid.type === 'paylines' ? (config.grid.payline_count ?? null) : null,
    gridType: config.grid.type,
  })
  syncControlsState()

  // Create win presenter for win highlights and pulse animation
  winPresenter = new WinPresenter({
    reelCount: config.grid.reels,
    rowCount: config.grid.rows,
    symbolWidth,
    symbolHeight,
    reelGap,
  })
  if (winOverlay) {
    winOverlay.addChild(winPresenter.container)
  }

  // Connect ticker for frame-by-frame reel and win animation
  if (app) {
    app.ticker.add(() => {
      if (!reelDisplay) return
      const deltaMs = app!.ticker.deltaMS

      // Spin animation
      if (spinAnimator && spinAnimator.isSpinning) {
        const offsets = spinAnimator.update(deltaMs)
        // update() may fire the completion callback which resets reel positions,
        // so only apply scroll offsets if still spinning
        if (spinAnimator.isSpinning) {
          for (let r = 0; r < offsets.length; r++) {
            const reel = reelDisplay.getReel(r)
            if (reel) {
              reel.y = -offsets[r]
            }
          }
        }
      }

      // Win presentation animation (pulse + dim)
      if (winPresenter && winPresenter.isActive) {
        const frame = winPresenter.update(deltaMs)
        const reels = config.grid.reels
        const rows = config.grid.rows

        for (let r = 0; r < reels; r++) {
          for (let row = 0; row < rows; row++) {
            const sym = reelDisplay.getSymbolAt(r, row)
            if (!sym) continue

            if (frame.winPositions.has(`${r}:${row}`)) {
              // Winning symbol: pulse scale, full alpha
              sym.alpha = 1.0
              sym.scale.set(frame.pulseScale)
            } else {
              // Non-winning symbol: dimmed, normal scale
              sym.alpha = winPresenter.dimAlpha
              sym.scale.set(1)
            }
          }
        }

        // Reset scales and alpha when presentation completes
        if (frame.isComplete) {
          for (let r = 0; r < reels; r++) {
            for (let row = 0; row < rows; row++) {
              const sym = reelDisplay.getSymbolAt(r, row)
              if (sym) {
                sym.alpha = 1.0
                sym.scale.set(1)
              }
            }
          }
        }
      }
    })
  }
}

// ─── Win presentation ───
function showWins(wins: WinDisplayData[]): void {
  if (!winPresenter) return
  winPresenter.showWins(wins)
}

function clearWins(): void {
  if (!winPresenter || !reelDisplay) return
  winPresenter.clear()

  // Reset all symbol alpha and scale
  const reels = slotConfig.value?.grid.reels ?? 5
  const rows = slotConfig.value?.grid.rows ?? 3
  for (let r = 0; r < reels; r++) {
    for (let row = 0; row < rows; row++) {
      const sym = reelDisplay.getSymbolAt(r, row)
      if (sym) {
        sym.alpha = 1.0
        sym.scale.set(1)
      }
    }
  }
}

// ─── Spin control ───
function triggerSpin(
  targetPositions: number[],
  finalGrid: string[][],
  wins?: WinDisplayData[],
  winData?: { payout: number; spinWins: SpinWinData[] },
): void {
  if (!spinAnimator || !reelDisplay || spinAnimator.isSpinning) return

  // Clear any active win presentation before spinning
  clearWins()
  clearWinDisplay()

  const config = slotConfig.value
  if (!config) return

  const rowCount = config.grid.rows

  // Build extended reel strips for smooth scrolling animation.
  // Each reel gets extra symbols below the visible area: random filler symbols
  // in the middle, and the final result symbols placed at the exact landing
  // position so the spin decelerates naturally into the result with no jump.
  for (let r = 0; r < config.grid.reels; r++) {
    const target = targetPositions[r]
    // Extra symbols: 'target' for scroll distance + 1 for bounce overshoot padding
    const extraCount = target + 1
    const extraSymbols: string[] = []
    const finalStartIdx = target - rowCount

    for (let i = 0; i < extraCount; i++) {
      if (i >= finalStartIdx && i < finalStartIdx + rowCount) {
        // Place final result symbols at the landing position
        extraSymbols.push(finalGrid[r][i - finalStartIdx])
      } else {
        // Random filler symbols for the scrolling blur
        const symIdx = Math.floor(Math.random() * config.symbols.length)
        extraSymbols.push(config.symbols[symIdx].id)
      }
    }

    reelDisplay.extendReelStrip(r, extraSymbols)
  }

  spinAnimator.startSpin(targetPositions, () => {
    // Snap to final symbols when animation completes
    if (reelDisplay && finalGrid.length > 0) {
      // Reset reel y-offsets and clean up extended strips
      for (let r = 0; r < (slotConfig.value?.grid.reels ?? 5); r++) {
        const reel = reelDisplay.getReel(r)
        if (reel) reel.y = 0
        reelDisplay.clearSpinStrip(r)
      }
      reelDisplay.setSymbols(finalGrid)

      // Show win presentation if there are wins
      if (wins && wins.length > 0) {
        showWins(wins)
      }

      // Show win amount display
      if (winData && winData.payout > 0) {
        showWinDisplay(winData.payout, winData.spinWins)
      }
    }

    // Notify game controls that spin completed
    onSpinComplete()
  })
}

// ─── Helpers ───
function scaleToFill(sprite: Sprite, targetW: number, targetH: number): void {
  const texW = sprite.texture.width
  const texH = sprite.texture.height
  if (texW === 0 || texH === 0) return

  const scale = Math.max(targetW / texW, targetH / texH)
  sprite.scale.set(scale)
  sprite.x = (targetW - texW * scale) / 2
  sprite.y = (targetH - texH * scale) / 2
}

function parseColor(hex: string): number {
  const cleaned = hex.replace('#', '')
  return parseInt(cleaned, 16) || 0x0d1b2a
}

// ─── Main initialization flow ───
async function initialize(): Promise<void> {
  isLoading.value = true
  loadingError.value = null
  loadingProgress.value = 0
  loadingStatus.value = 'Initializing...'

  try {
    // Step 1: Load slot config
    const config = await loadSlotConfig()
    if (config) {
      slotConfig.value = config
    } else {
      // No config available — show error with retry
      loadingError.value = 'Could not load slot configuration. Please try again.'
      return
    }

    // Step 2: Initialize PixiJS
    loadingStatus.value = 'Setting up canvas...'
    loadingProgress.value = 15
    const pixiReady = initPixi()
    if (!pixiReady) {
      loadingError.value = 'Failed to initialize game canvas.'
      return
    }

    // Step 3: Load assets
    await loadAssets(config)

    // Step 4: Render background + logo
    loadingStatus.value = 'Rendering...'
    loadingProgress.value = 92
    renderBackground(config)
    renderLogo()

    // Step 5: Setup reels
    loadingStatus.value = 'Setting up reels...'
    loadingProgress.value = 95
    setupReels(config)

    // Step 6: Setup responsive resize
    setupResize()

    // Step 7: Layout the scene
    if (app) {
      const w = app.renderer.width / (app.renderer.resolution || 1)
      const h = app.renderer.height / (app.renderer.resolution || 1)
      layoutScene(w, h)
    }

    // Done
    loadingProgress.value = 100
    loadingStatus.value = 'Ready!'
    await new Promise((resolve) => setTimeout(resolve, 200))
    isLoading.value = false
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Unknown error'
    loadingError.value = `Failed to load game: ${msg}`
  }
}

async function retryLoading(): Promise<void> {
  loadingError.value = null
  await initialize()
}

// ─── Lifecycle ───
onMounted(() => {
  setupMobileDetection()
  initialize()
})

onBeforeUnmount(() => {
  clearWinDisplay()
  teardownMobileDetection()
  resizeObserver?.disconnect()
  resizeObserver = null
  spinAnimator?.reset()
  spinAnimator = null
  winPresenter?.destroy()
  winPresenter = null
  reelDisplay?.destroy()
  reelDisplay = null
  gameControls = null
  if (app) {
    app.destroy(true, { children: true, texture: true, baseTexture: true })
    app = null
  }
  bgContainer = null
  logoContainer = null
  reelContainer = null
  winOverlay = null
})

// ─── Expose for testing ───
defineExpose({
  slotConfig,
  isLoading,
  loadingProgress,
  loadingStatus,
  loadingError,
  retryLoading,
  initialize,
  triggerSpin,
  showWins,
  clearWins,
  gameControls: () => gameControls,
  currentCoinValue,
  currentTotalStake,
  spinButtonText,
  turboEnabled,
  autoSpinActive,
  autoSpinRemaining,
  isMobile,
  showLandscapeWarning,
  onCoinUp,
  onCoinDown,
  onCoinSelect,
  onBetPreset,
  onToggleTurbo,
  onSpinClick,
  onStartAutoSpin,
  onStopAutoSpin,
  lastWinAmount,
  winDisplay,
  showWinDisplay,
  clearWinDisplay,
})
</script>

<style scoped>
.slot-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ---- Topbar ---- */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  transition: background 0.15s;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

.btn-back:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: background 0.15s;
  border: none;
}

.icon-btn:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.icon-btn.active {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

/* ---- Body ---- */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ---- Canvas Area ---- */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
}

.canvas-bg-glow {
  position: absolute;
  pointer-events: none;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 231, 1, 0.05) 0%, transparent 65%);
}

/* ---- PixiJS Mount ---- */
.slot-canvas-mount {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.slot-canvas-mount :deep(canvas) {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* ---- Loading Screen ---- */
.slot-loading-screen {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-game, #0d1b2a);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 320px;
  width: 100%;
  padding: 0 24px;
}

.loading-logo {
  font-size: 48px;
  animation: pulse 1.5s ease-in-out infinite;
}

.loading-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 18px;
  color: var(--text-primary, #fff);
  text-align: center;
}

.loading-bar-container {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
  overflow: hidden;
}

.loading-bar-fill {
  height: 100%;
  background: var(--gradient-accent, linear-gradient(90deg, #00e701, #1475e1));
  border-radius: 9999px;
  transition: width 0.3s ease;
}

.loading-status {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.loading-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.loading-error p {
  font-size: 13px;
  color: var(--color-danger, #ed4245);
  text-align: center;
}

.btn-retry {
  padding: 8px 24px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.btn-retry:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
}

/* ---- Sidebar ---- */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}

.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}

.game-tab:hover:not(.active) {
  color: var(--text-secondary, #b1bad3);
}

.game-tab-content {
  display: none;
  flex: 1;
  overflow-y: auto;
  flex-direction: column;
  padding: 14px;
}

.game-tab-content.active {
  display: flex;
}

/* ---- Controls: Labels ---- */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

.ctrl-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 12px 0;
}

/* ---- Coin Value ---- */
.coin-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.coin-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.15s;
  font-family: inherit;
}

.coin-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  border-color: rgba(255, 255, 255, 0.15);
}

.coin-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.coin-value-display {
  flex: 1;
  text-align: center;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

/* ---- Lines & Stake ---- */
.lines-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.lines-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.stake-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.stake-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

/* ---- Bet Presets ---- */
.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
  text-align: center;
  font-family: inherit;
}

.bet-short:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

/* ---- Toggle ---- */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.toggle-label {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  font-weight: 600;
}

.toggle {
  width: 36px;
  height: 20px;
  background: var(--bg-primary, #0f1923);
  border-radius: 9999px;
  position: relative;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: background 0.15s;
  flex-shrink: 0;
}

.toggle.on {
  background: var(--accent-primary, #00e701);
  border-color: var(--accent-primary, #00e701);
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: var(--color-on-dark, #fff);
  border-radius: 50%;
  transition: transform 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.toggle.on .toggle-thumb {
  transform: translateX(16px);
}

/* ---- Action Button ---- */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}

.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-action:active:not(:disabled) {
  transform: translateY(0);
}

.btn-action.btn-stop {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
}

.btn-action.btn-stop:hover:not(:disabled) {
  box-shadow: 0 0 20px rgba(237, 66, 69, 0.4);
  background: var(--color-danger, #d63638);
}

/* ---- Stat Pills ---- */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-top: 8px;
}

.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}

.sp-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.sp-value.gold {
  color: var(--accent-gold, #ffd700);
}

/* ---- Auto-spin ---- */
.auto-count-row {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.auto-count-btn {
  flex: 1;
  padding: 8px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  text-align: center;
  font-family: inherit;
}

.auto-count-btn:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--text-primary, #fff);
}

.auto-count-btn.selected {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

.auto-field {
  margin-bottom: 12px;
}

.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.auto-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 12px 9px 24px;
  color: var(--text-primary, #fff);
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  transition: border-color 0.15s;
}

.auto-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

/* ---- Rules ---- */
.rules-section {
  margin-bottom: 16px;
}

.rules-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.rules-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.rules-item {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  padding-left: 14px;
  position: relative;
  line-height: 1.55;
}

.rules-item::before {
  content: '\25B8';
  position: absolute;
  left: 0;
  color: var(--accent-primary, #00e701);
  font-size: 10px;
  top: 1px;
}

/* ---- Win Display Overlay ---- */
.win-display-overlay {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 32px;
  background: rgba(0, 0, 0, 0.75);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  pointer-events: none;
  min-width: 160px;
}

.win-display-overlay.nice {
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.15);
}

.win-display-overlay.big {
  border-color: rgba(255, 215, 0, 0.6);
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.25);
}

.win-display-overlay.mega {
  border-color: rgba(255, 215, 0, 0.8);
  box-shadow: 0 0 40px rgba(255, 215, 0, 0.35);
  animation: win-glow 1s ease-in-out infinite;
}

.win-display-overlay.ultra {
  border-color: rgba(255, 215, 0, 1);
  box-shadow: 0 0 60px rgba(255, 215, 0, 0.5), 0 0 100px rgba(255, 215, 0, 0.2);
  animation: win-glow 0.6s ease-in-out infinite;
}

.win-display-amount {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 28px;
  font-weight: 700;
  color: var(--accent-gold, #ffd700);
  text-shadow: 0 0 12px rgba(255, 215, 0, 0.5);
  white-space: nowrap;
}

.win-display-overlay.mega .win-display-amount,
.win-display-overlay.ultra .win-display-amount {
  font-size: 34px;
}

.win-display-detail {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
  text-align: center;
}

/* Win display enter/leave transitions */
.win-display-enter-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.win-display-leave-active {
  transition: all 0.3s ease-in;
}

.win-display-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(12px) scale(0.85);
}

.win-display-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}

/* ---- Last Win in Sidebar ---- */
.last-win-display .ctrl-label {
  color: var(--accent-gold, #ffd700);
}

.stake-value.gold {
  color: var(--accent-gold, #ffd700);
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
}

/* ---- Animations ---- */
@keyframes win-glow {
  0%, 100% {
    box-shadow: 0 0 30px rgba(255, 215, 0, 0.25);
  }
  50% {
    box-shadow: 0 0 50px rgba(255, 215, 0, 0.45);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ---- Landscape Warning ---- */
.landscape-warning {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--bg-primary, #0f1923);
  display: flex;
  align-items: center;
  justify-content: center;
}

.landscape-warning-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding: 24px;
}

.landscape-warning-icon {
  font-size: 64px;
  animation: rotatePhone 2s ease-in-out infinite;
}

.landscape-warning-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  max-width: 240px;
  line-height: 1.5;
}

@keyframes rotatePhone {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-90deg);
  }
  75% {
    transform: rotate(-90deg);
  }
}

/* ---- Responsive: Tablet ---- */
@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }

  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 280px;
  }
}

/* ---- Responsive: Mobile (≤600px) ---- */
@media (max-width: 600px) {
  .slot-game {
    height: 100%;
    overflow: hidden;
  }

  .win-display-overlay {
    bottom: 8px;
    padding: 8px 20px;
    min-width: 120px;
  }

  .win-display-amount {
    font-size: 22px;
  }

  .win-display-overlay.mega .win-display-amount,
  .win-display-overlay.ultra .win-display-amount {
    font-size: 26px;
  }

  .win-display-detail {
    font-size: 11px;
  }

  /* Compact topbar */
  .game-topbar {
    height: 40px;
    padding: 0 10px;
    gap: 8px;
  }

  .btn-back {
    padding: 4px 8px;
    font-size: 12px;
  }

  .game-title-bar {
    font-size: 12px;
    gap: 6px;
  }

  .game-tag {
    font-size: 9px;
    padding: 1px 6px;
  }

  .icon-btn {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    font-size: 16px;
  }

  /* Body: stacked vertical layout */
  .game-body {
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  /* Canvas takes ~55% of available height on mobile */
  .game-canvas-area {
    flex: none;
    height: 55vh;
    min-height: 200px;
    max-height: 55vh;
  }

  /* Sidebar becomes bottom panel, scrollable */
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    flex: 1;
    max-height: none;
    overflow: hidden;
  }

  /* Tabs: larger touch targets */
  .game-tabs {
    flex-shrink: 0;
  }

  .game-tab {
    padding: 12px 4px;
    font-size: 12px;
    min-height: 44px;
  }

  /* Tab content: full-width controls */
  .game-tab-content {
    padding: 12px;
  }

  /* Coin buttons: 44×44 minimum */
  .coin-btn {
    width: 44px;
    height: 44px;
    font-size: 20px;
    border-radius: 8px;
  }

  .coin-value-display {
    font-size: 18px;
  }

  /* Bet presets: full-width, taller */
  .bet-shortcuts {
    gap: 6px;
    margin-bottom: 10px;
  }

  .bet-short {
    padding: 10px 0;
    font-size: 13px;
    min-height: 44px;
    border-radius: 8px;
  }

  /* Toggle: larger for touch */
  .toggle {
    width: 44px;
    height: 24px;
  }

  .toggle-thumb {
    width: 18px;
    height: 18px;
    top: 2px;
    left: 2px;
  }

  .toggle.on .toggle-thumb {
    transform: translateX(20px);
  }

  .toggle-label {
    font-size: 13px;
  }

  /* Action button: full-width, large touch target */
  .btn-action {
    padding: 16px;
    font-size: 16px;
    min-height: 50px;
    border-radius: 10px;
  }

  /* Auto-spin count buttons: taller for touch */
  .auto-count-row {
    gap: 6px;
    margin-bottom: 10px;
  }

  .auto-count-btn {
    padding: 10px 0;
    font-size: 13px;
    min-height: 44px;
    border-radius: 8px;
  }

  /* Auto inputs: larger touch targets */
  .auto-input {
    padding: 12px 12px 12px 28px;
    font-size: 15px;
    min-height: 44px;
  }

  /* Stat pills: compact on mobile */
  .stat-pills {
    gap: 4px;
    margin-top: 6px;
  }

  .stat-pill {
    padding: 6px 8px;
  }

  /* Stake display: larger text */
  .stake-display {
    margin-bottom: 8px;
  }

  .stake-value {
    font-size: 18px;
  }

  /* Lines display */
  .lines-display {
    margin-bottom: 6px;
  }

  /* Dividers: tighter spacing */
  .ctrl-divider {
    margin: 8px 0;
  }

  /* Rules: adjusted for mobile */
  .rules-list {
    gap: 8px;
  }

  .rules-item {
    font-size: 13px;
    line-height: 1.6;
  }
}
</style>
